// ─── API layer for the Query Builder ─────────────────────────────────────────
// All endpoint knowledge for this feature lives here; components call these
// named functions and never assemble URLs themselves. apiCall injects auth and
// returns { isSuccess, data, message }.
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";

const BASE = config.POSTLOGIN_API_URL_COMPANY;
const QB = `${BASE}query-builder`;

/**
 * Lists the company's locations for the builder's location scope picker. Uses
 * the same source as the Set Due Date screen so the two agree on the location
 * set; the picker's value is the location id, which matches the dataset's
 * `locationId` scope field.
 * @returns {Promise<object>} apiCall result whose data.data is the location array.
 */
export const listLocations = () =>
  apiCall(`${BASE}getSource`, {}, {}, "GET", true, false);

/**
 * Lists the datasets the builder can target, straight from the backend registry.
 * @returns {Promise<object>} apiCall result whose data is [{ key, label }].
 */
export const listDatasets = () =>
  apiCall(`${QB}/datasets`, {}, {}, "GET", true, false);

/**
 * Fetches a module's client-safe field metadata (fields, types, operators).
 * @param {string} module the module key (e.g. "DATA_MANAGEMENT").
 * @returns {Promise<object>} apiCall result whose data is the module metadata.
 */
export const getModuleMetadata = (module) =>
  apiCall(`${QB}/metadata`, {}, { module }, "GET", true, false);

/**
 * Runs a query config and returns one page of preview rows.
 * @param {object} queryConfig the query config (module, fields, filters).
 * @param {number} page 1-based page number.
 * @param {number} pageSize rows per page.
 * @returns {Promise<object>} apiCall result whose data is { data, pagination }.
 */
export const previewQuery = (queryConfig, page, pageSize) =>
  apiCall(
    `${QB}/preview?page=${page}&pageSize=${pageSize}`,
    {},
    queryConfig,
    "POST",
    true,
    false,
  );

/**
 * Lists the company's saved queries.
 * @returns {Promise<object>} apiCall result whose data is the saved queries.
 */
export const listSavedQueries = () =>
  apiCall(`${QB}/saved-queries`, {}, {}, "GET");

/**
 * Creates a saved query.
 * @param {string} name the saved-query name.
 * @param {object} queryConfig the query config to persist.
 * @returns {Promise<object>} apiCall result whose data is the created saved query.
 */
export const createSavedQuery = (name, queryConfig) =>
  apiCall(`${QB}/saved-queries`, {}, { name, config: queryConfig }, "POST");

/**
 * Deletes a saved query.
 * @param {number} id the saved-query id.
 * @returns {Promise<object>} apiCall result.
 */
export const deleteSavedQuery = (id) =>
  apiCall(`${QB}/saved-queries/${id}`, {}, {}, "DELETE");
